import { featureFlags } from '@seen/core/featureFlags'
import { getFilenameFromContentDisposition } from '@seen/core/utils'
import {
  type PdfExport,
  type PdfExportRequest,
  pdfExportPath,
} from '@seen/request-types/pdfExports'
import { useCallback } from 'react'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

function lambdaPath(request: PdfExportRequest): string {
  return request.kind === 'referral'
    ? '/orders/generate-pdf'
    : pdfExportPath(request)
}

function lambdaBody(request: PdfExportRequest) {
  switch (request.kind) {
    case 'referral':
      return { orderId: request.orderId }
    case 'facesheet':
      return request.scope ? { scope: request.scope } : {}
    case 'timeline':
      return { startDate: request.startDate, endDate: request.endDate }
    default:
      return {}
  }
}

export interface ExportPdf {
  exportPdf: (request: PdfExportRequest) => Promise<PdfExport>
  // True when exports go to the host app's /api/pdf endpoints.
  viaPortal: boolean
}

// Renders an export on the portal when portal-pdf-exports is on and the host
// app serves /api/pdf, else on the Lambda API. packet-pdf-exports picks the
// packet layout on the portal.
export function useExportPdf(): ExportPdf {
  const { seenApi, pdf } = useSeenApi()
  const portal = useFeatureFlag(featureFlags.portalPdfExports)
  const packet = useFeatureFlag(featureFlags.packetPdfExports)
  const portalPdf = portal ? pdf : undefined

  const exportPdf = useCallback(
    async (request: PdfExportRequest): Promise<PdfExport> => {
      if (portalPdf) {
        return portalPdf.exportPdf(
          packet && request.kind !== 'appointment-prep'
            ? { ...request, layout: 'packet' }
            : request
        )
      }
      if (request.kind === 'referral') {
        // The Lambda answers with the PDF base64-encoded as text.
        const response = await seenApi.post<string>(
          lambdaPath(request),
          lambdaBody(request)
        )
        return {
          blob: new Blob(
            [Uint8Array.from(atob(response.data), (c) => c.charCodeAt(0))],
            {
              type: 'application/pdf',
            }
          ),
          filename: undefined,
        }
      }
      const response = await seenApi.post<Blob>(
        lambdaPath(request),
        lambdaBody(request),
        { responseType: 'blob' }
      )
      return {
        blob: response.data,
        filename: getFilenameFromContentDisposition(
          response.headers['content-disposition']
        ),
      }
    },
    [portalPdf, packet, seenApi]
  )

  return { exportPdf, viaPortal: portalPdf !== undefined }
}
